import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Input } from '@/components/ui/input.js';
import { Badge } from '@/components/ui/badge.js';
import { Tag } from 'lucide-react';

interface AddMemoryModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddMemoryModal({ open, onClose, onSuccess }: AddMemoryModalProps) {
  const { api } = useAuth();
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) return;

    try {
      setIsSaving(true);
      const parsedTags = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await api.createMemory({
        content: content.trim(),
        tags: parsedTags.length > 0 ? parsedTags : undefined,
      });

      // Reset and close
      setContent('');
      setTags('');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to create memory:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setContent('');
    setTags('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Memory</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Content */}
          <div className="space-y-2">
            <label className="text-sm font-medium">What should I remember?</label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="E.g., I prefer concise responses, I'm a software engineer..."
              className="min-h-[120px] resize-none"
              autoFocus
            />
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Tags (optional)
            </label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="work, preferences, personal (comma-separated)"
            />
            {tags && (
              <div className="flex flex-wrap gap-2 pt-2">
                {tags
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean)
                  .map((tag, index) => (
                    <Badge key={index} variant="secondary">
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!content.trim() || isSaving}>
            {isSaving ? 'Saving...' : 'Save Memory'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
